/**
 * useWebPush — tarayıcı masaüstü/mobil bildirimleri
 *
 * - Oturum açılınca sessizce izin ister (ilk ziyarette prompt).
 * - WS'den gelen "invalidate" mesajlarını yakalar, kullanıcının rolüne göre
 *   anlamlı olanlar için `new Notification(...)` tetikler.
 * - Kullanıcı sekme kapalıyken bile tarayıcı bildirimi gösterir (OS seviyesi).
 * - Hedefleme kuralları: Mesela "cutting_task_create" sadece kesimciye,
 *   "pending_order_create" sadece planner/admin'e düşer.
 */
import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";

const AUDIENCE = {
  // kesim görevleri → kesimciler (atama + oluşturma)
  cutting_task_create: ["cutter"],
  cutting_task_assign: ["cutter"],
  cutting_task_item_update: ["cutter"],
  // kesim tamamlanınca → planner & admin & accounting
  cutting_task_delivered: ["admin", "planner", "accounting"],
  cutting_task_redirect_shipping: ["admin", "planner", "stock_in"],
  // bekleyen sipariş oluşturma → planlama + yönetici
  pending_order_create: ["admin", "planner"],
  // fabrika siparişi oluşturulunca → admin
  factory_order_create: ["admin", "planner"],
  // perakende sipariş → admin, planner
  retail_order_create: ["admin", "planner"],
  // stok giriş → tüm ekip (opsiyonel; şimdilik admin+planner)
  roll_split: ["admin", "stock_in"],
  roll_bulk_create: ["admin", "stock_in", "planner"],
  // iade / defo
  return_create: ["admin", "accounting"],
  // aylık istatistik raporu → statistics_view yetkisi olanlar
  monthly_report_ready: "statistics_view",
  weekly_summary_ready: "statistics_view",
};

const TITLES = {
  cutting_task_create: "Yeni Kesim Görevi",
  cutting_task_assign: "Sana Yeni Kesim Görevi Atandı",
  cutting_task_item_update: "Kesim Görevi Güncellendi",
  cutting_task_delivered: "Kesim Tamamlandı",
  cutting_task_redirect_shipping: "Kesim → Sevkiyat/Mağaza",
  pending_order_create: "Yeni Müşteri Siparişi",
  factory_order_create: "Yeni Fabrika Siparişi",
  retail_order_create: "Yeni Perakende Satış",
  roll_split: "Top Bölündü",
  roll_bulk_create: "Yeni Stok Girişi",
  return_create: "Yeni İade",
  monthly_report_ready: "Aylık İstatistik Raporu Hazır",
  weekly_summary_ready: "Haftalık Özet",
};

export default function useWebPush() {
  const { user } = useAuth();
  const askedRef = useRef(false);

  // Permission isteği (login sonrası, bir kere)
  useEffect(() => {
    if (!user) return;
    if (!("Notification" in window)) return;
    if (askedRef.current) return;
    askedRef.current = true;
    if (Notification.permission === "default") {
      // UX: kullanıcıya ön-bilgi vermeden hemen sormak brow spam uyarısı almayalım
      // Küçük gecikme + sadece bir kez
      setTimeout(() => {
        try { Notification.requestPermission(); } catch {}
      }, 2000);
    }
  }, [user]);

  // WS invalidate olaylarını dinle, bildirim gönder
  useEffect(() => {
    if (!user) return;
    if (!("Notification" in window)) return;

    const resources = Object.keys(AUDIENCE)
      .map((a) => a.split("_").slice(0, -1).join("_")) // "cutting_task_create" → "cutting_task"
      .filter((v, i, a) => a.indexOf(v) === i);

    const handler = (ev) => {
      if (Notification.permission !== "granted") return;
      const data = ev.detail || {};
      const action = data.action;
      if (!action) return;
      const audience = AUDIENCE[action];
      if (!audience) return;
      if (typeof audience === "string" ? !can(user, audience) : !audience.includes(user.role)) return;

      const title = TITLES[action] || "Yeni bildirim";
      const body = buildBody(action, data);
      try {
        const notif = new Notification(title, {
          body,
          icon: "/mossline-logo.png",
          tag: `${action}:${data.entity_id || ""}`,
          renotify: false,
          silent: false,
        });
        notif.onclick = () => {
          window.focus();
          const url = routeForAction(action);
          if (url) window.location.href = url;
          notif.close();
        };
        // 8 saniye sonra kapat
        setTimeout(() => notif.close(), 8000);
      } catch {}
    };

    const subs = resources.map((r) => {
      const evt = `rt:${r}`;
      window.addEventListener(evt, handler);
      return [evt, handler];
    });
    return () => {
      subs.forEach(([evt, h]) => window.removeEventListener(evt, h));
    };
  }, [user]);
}

function buildBody(action, data) {
  const id = (data.entity_id || "").slice(0, 8);
  switch (action) {
    case "cutting_task_create":
    case "cutting_task_assign":
      return `Görev kodu: ${id}... Kesim görevleri ekranından aç.`;
    case "cutting_task_delivered":
      return "Bir kesim görevi tamamlandı — sevkiyat / mağaza seçimi gerekebilir.";
    case "pending_order_create":
      return "Yeni bir müşteri siparişi eklendi.";
    case "factory_order_create":
      return "Yeni bir fabrika siparişi eklendi.";
    case "retail_order_create":
      return "Yeni bir perakende satış kaydedildi.";
    case "roll_bulk_create":
      return "Yeni toplar sisteme eklendi.";
    case "roll_split":
      return "Bir top bölündü.";
    case "return_create":
      return "Yeni bir iade kaydı oluşturuldu.";
    case "weekly_summary_ready":
      return `${data.label || "Geçen hafta"} özeti hazır — görmek için tıkla.`;
    case "monthly_report_ready":
      return `${data.label || "Geçen ay"} raporu hazır — PDF'i indirmek için tıkla.`;
    default:
      return "Sistemde yeni bir güncelleme var.";
  }
}

function routeForAction(action) {
  if (action.startsWith("cutting_task")) return "/kesim-gorevleri";
  if (action.startsWith("pending_order")) return "/bekleyen";
  if (action.startsWith("factory_order")) return "/fabrika-siparisleri";
  if (action.startsWith("retail_order")) return "/siparisler";
  if (action.startsWith("roll")) return "/stok";
  if (action.startsWith("return")) return "/iadeler";
  if (action.startsWith("weekly_summary")) return "/istatistik?period=last_week";
  if (action.startsWith("monthly_report")) return "/istatistik#monthly-reports";
  return null;
}
